import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { NETWORK } from '../config';
import './Investment.css';

function Investment({ contract, tokenContract, userId, account, setUserId, setError }) {
  const [sponsorId, setSponsorId] = useState('');
  const [position, setPosition] = useState('0');
  const [amount, setAmount] = useState('');
  const [tokenBalance, setTokenBalance] = useState('0');
  const [allowance, setAllowance] = useState('0');
  const [loading, setLoading] = useState(false);
  const [txStatus, setTxStatus] = useState('');

  // Parse URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get('ref');
    const posParam = urlParams.get('pos');
    
    if (refParam) {
      setSponsorId(refParam);
    }
    if (posParam === '0' || posParam === '1' || posParam === 'left' || posParam === 'right') {
      if (posParam === 'left') {
        setPosition('0');
      } else if (posParam === 'right') {
        setPosition('1');
      } else {
        setPosition(posParam);
      }
    }
  }, []);

  const loadBalances = async () => {
    if (!tokenContract || !account) return;

    try {
      const balance = await tokenContract.balanceOf(account);
      setTokenBalance(ethers.utils.formatEther(balance));

      const allow = await tokenContract.allowance(account, NETWORK.contractAddress);
      setAllowance(ethers.utils.formatEther(allow));
    } catch (err) {
      console.error('Error loading balances:', err);
    }
  };

  useEffect(() => {
    loadBalances();
    const interval = setInterval(loadBalances, 10000);
    return () => clearInterval(interval);
  }, [tokenContract, account]);

  const handleInvest = async () => {
    if (!contract || !tokenContract || !amount) return;

    const usdtAmount = parseFloat(amount);
    if (usdtAmount < 100 || usdtAmount % 100 !== 0) {
      setError('Amount must be minimum $100 and multiple of $100');
      return;
    }

    if (usdtAmount > parseFloat(tokenBalance)) {
      setError('Insufficient USDT balance');
      return;
    }

    setLoading(true);
    
    try {
      const tokenAmountWei = ethers.utils.parseEther(amount);
      
      // Check if approval is needed
      if (parseFloat(amount) > parseFloat(allowance)) {
        setTxStatus('Step 1/2: Approving USDT...');
        const maxApproval = ethers.constants.MaxUint256;
        const approveTx = await tokenContract.approve(NETWORK.contractAddress, maxApproval);
        await approveTx.wait();
        setTxStatus('Approval confirmed. Step 2/2: Processing investment...');
        await loadBalances();
      } else {
        setTxStatus('Processing investment...');
      }

      let tx;

      if (userId === 0) {
        const sId = sponsorId ? parseInt(sponsorId) : 0;
        const pos = parseInt(position);
        tx = await contract.registerAndInvest(sId, pos, tokenAmountWei);
        setTxStatus('Registration and investment submitted...');
      } else {
        tx = await contract.invest(tokenAmountWei);
        setTxStatus('Investment submitted...');
      }

      await tx.wait();
      setTxStatus('✅ Investment successful!');

      if (userId === 0) {
        const newId = await contract.addressToId(account);
        setUserId(newId.toNumber());
        // Clear URL parameters after successful registration
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      setAmount('');
      await loadBalances();

      setTimeout(() => setTxStatus(''), 5000);
    } catch (err) {
      console.error('Investment error:', err);
      setError(err.reason || err.message || 'Transaction failed');
      setTxStatus('');
    } finally {
      setLoading(false);
    }
  };

  const packages = [100, 200, 500, 1000, 2000, 5000, 10000];

  return (
    <div className="investment">
      <div className="section-header">
        <h3>
          <span className="section-icon">💎</span>
          {userId === 0 ? 'Register & Invest' : 'Add Investment'}
        </h3>
      </div>

      <div className="balance-display">
        <div className="balance-item">
          <span className="balance-label">USDT Balance</span>
          <span className="balance-value">{parseFloat(tokenBalance).toFixed(2)}</span>
        </div>
      </div>

      {userId === 0 && (
        <>
          <div className="form-group">
            <label>Sponsor ID</label>
            <input
              type="number"
              placeholder="Optional - leave empty for default"
              value={sponsorId}
              onChange={(e) => setSponsorId(e.target.value)}
              disabled={loading}
            />
            {sponsorId && (
              <div className="input-hint success">✓ Sponsor ID set from referral link</div>
            )}
          </div>

          <div className="form-group">
            <label>Position</label>
            <div className="position-selector">
              <button
                className={position === '0' ? 'active left' : ''}
                onClick={() => setPosition('0')}
                disabled={loading}
              >
                ⬅️ Left
              </button>
              <button
                className={position === '1' ? 'active right' : ''}
                onClick={() => setPosition('1')}
                disabled={loading}
              >
                Right ➡️
              </button>
            </div>
          </div>
        </>
      )}

      <div className="form-group">
        <label>Amount (USDT)</label>
        <input
          type="number"
          placeholder="Min $100"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          disabled={loading}
          step="100"
          min="100"
        />
      </div>

      <div className="quick-amounts">
        {packages.map((pkg) => (
          <button
            key={pkg}
            onClick={() => setAmount(pkg.toString())}
            disabled={loading}
            className={amount === pkg.toString() ? 'selected' : ''}
          >
            ${pkg}
          </button>
        ))}
      </div>

      {amount && parseFloat(amount) >= 100 && (
        <div className="investment-summary">
          <div className="summary-row">
            <span>Investment Amount</span>
            <span>${parseFloat(amount).toFixed(2)} USDT</span>
          </div>
          <div className="summary-row">
            <span>Max ROI (300%)</span>
            <span>${(parseFloat(amount) * 3).toFixed(2)} USDT</span>
          </div>
          {parseFloat(amount) > parseFloat(allowance) && (
            <div className="summary-row approval-needed">
              <span>⚠️ Approval Required</span>
              <span>Will approve first</span>
            </div>
          )}
        </div>
      )}

      {txStatus && (
        <div className="tx-status">
          <div className="status-spinner"></div>
          {txStatus}
        </div>
      )}

      <div className="action-buttons">
        <button
          className="invest-btn"
          onClick={handleInvest}
          disabled={loading || !amount || parseFloat(amount) < 100}
        >
          {loading ? 'Processing...' : userId === 0 ? 'Register & Invest' : 'Invest Now'}
        </button>
      </div>
    </div>
  );
}

export default Investment;
